import React from 'react';

import Accordion from 'components/Accordion';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import CategoriaModelos from 'components/Windx/ControlPanel/Models/CategoriaModelos';
import {
  CATEGORIA_LABEL,
  Categoria,
  Modelo,
  modeloElegivel,
  ORDEM_CATEGORIAS,
  TipoRodada,
} from 'helper/windx';

export type ModoSelecaoModelos = 'checkbox' | 'radio';

interface ModelsProps {
  modelos: Modelo[];
  idsSelecionados: number[];
  onChange: (ids: number[]) => void;
  // WINDX-048: removidos `onSelecionarTodos` e `onLimpar`. Botoes "Nenhum"
  // e "Todos" agora sao por grupo (cima de cada secao do accordion) e
  // operam via `onChange` direto, sem precisar de callback do parent.
  idsIndisponiveis?: number[];
  // ADR-018: `data_rodada` vem como string ISO 'YYYY-MM-DD' das rotas
  // /windx/* desde o MR BE !91. Tipo simplificado de
  // `string | number | Date` para apenas `string`.
  datasRodadaPorId?: Record<number, string>;
  tipoRodada?: TipoRodada;
  /**
   * WINDX-028 (WEOL-SM): seletor passa a radio (escolha unica). Default
   * `'checkbox'` preserva o comportamento atual do WEOL-SH.
   */
  modoSelecao?: ModoSelecaoModelos;
}

// WINDX-048: sentinel string para o grupo que recebe modelos sem categoria
// quando NENHUM modelo tem categoria conhecida (estado transitorio pre-deploy
// do BE !116 ou retrocompat). Quando ha ao menos 1 modelo com categoria, os
// modelos null sao anexados ao primeiro grupo conhecido (ordem canonica).
const CATEGORIA_DEFAULT = 'default';

interface GrupoModelos {
  // chave usada em testid + key React. Categoria conhecida ou sentinel
  // 'default' para o grupo de fallback (todos sem categoria).
  chave: Categoria | typeof CATEGORIA_DEFAULT;
  // label visivel no header da secao. Vazio quando ha apenas 1 grupo
  // (UX do issue: "SM exibe 1 grupo sem cabecalho").
  label: string;
  modelos: Modelo[];
}

/**
 * Agrupa modelos por `categoria` na ordem canonica do contrato.
 *
 * Regras (WINDX-048):
 * 1. Modelos com `categoria` conhecida formam grupos na ordem
 *    curto_prazo -> medio_prazo -> climatologia -> anos_historicos
 *    (`ORDEM_CATEGORIAS`).
 * 2. Modelos sem `categoria` (null/undefined ou valor desconhecido pelo FE)
 *    sao anexados ao PRIMEIRO grupo conhecido (decisao PO 2026-05-26).
 * 3. Se nenhum modelo tem categoria conhecida (BE pre-deploy), todos caem
 *    em 1 grupo unico com chave 'default' e sem header visivel.
 */
export function agruparPorCategoria(modelos: Modelo[]): GrupoModelos[] {
  const mapaPorCategoria = new Map<Categoria, Modelo[]>();
  const semCategoria: Modelo[] = [];

  modelos.forEach((m) => {
    const cat = m.categoria;
    if (cat && (ORDEM_CATEGORIAS as readonly string[]).includes(cat)) {
      const arr = mapaPorCategoria.get(cat) ?? [];
      arr.push(m);
      mapaPorCategoria.set(cat, arr);
    } else {
      semCategoria.push(m);
    }
  });

  const grupos: GrupoModelos[] = [];
  ORDEM_CATEGORIAS.forEach((cat) => {
    const items = mapaPorCategoria.get(cat);
    if (items && items.length > 0) {
      grupos.push({ chave: cat, label: CATEGORIA_LABEL[cat], modelos: items });
    }
  });

  if (semCategoria.length > 0) {
    if (grupos.length > 0) {
      // Anexa ao 1o grupo conhecido (decisao PO).
      grupos[0] = {
        ...grupos[0],
        modelos: [...grupos[0].modelos, ...semCategoria],
      };
    } else {
      // Fallback: nenhum modelo categorizado. 1 grupo unico sem header.
      grupos.push({ chave: CATEGORIA_DEFAULT, label: '', modelos: semCategoria });
    }
  }

  return grupos;
}

export default function Models({
  modelos,
  idsSelecionados,
  onChange,
  idsIndisponiveis,
  datasRodadaPorId,
  tipoRodada,
  modoSelecao = 'checkbox',
}: ModelsProps) {
  const indisponiveisSet = new Set(idsIndisponiveis ?? []);
  const elegivel = (m: Modelo): boolean => modeloElegivel(m, indisponiveisSet, tipoRodada);

  const selecionarTodosDoGrupo = (modelosDoGrupo: Modelo[]) => {
    const idsElegiveis = modelosDoGrupo.filter(elegivel).map((m) => m.id);
    const merged = Array.from(new Set([...idsSelecionados, ...idsElegiveis]));
    onChange(merged);
  };

  const limparGrupo = (modelosDoGrupo: Modelo[]) => {
    const idsDoGrupo = new Set(modelosDoGrupo.map((m) => m.id));
    onChange(idsSelecionados.filter((id) => !idsDoGrupo.has(id)));
  };

  const grupos = agruparPorCategoria(modelos);

  return (
    <Accordion.Item eventKey="models">
      <Accordion.Header>
        <TitleSemiBold>Modelos</TitleSemiBold>
      </Accordion.Header>
      <Accordion.Body>
        {grupos.map((grupo) => (
          <CategoriaModelos
            key={grupo.chave}
            chave={grupo.chave}
            titulo={grupo.label}
            modelos={grupo.modelos}
            idsSelecionados={idsSelecionados}
            idsIndisponiveis={indisponiveisSet}
            datasRodadaPorId={datasRodadaPorId}
            tipoRodada={tipoRodada}
            modoSelecao={modoSelecao}
            onChange={onChange}
            onSelecionarTodos={selecionarTodosDoGrupo}
            onLimpar={limparGrupo}
          />
        ))}
      </Accordion.Body>
    </Accordion.Item>
  );
}
